import { ChangeEvent, FC, useState } from 'react'
import {
  BooleanInput, Edit, SimpleForm, TextInput, required, useTranslate, useRecordContext,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { Box, Button, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { McpServerOut } from '../../types/apiRequests'
import AdvancedSettings from './AdvancedSettings'

const SpecUrlField: FC = () => {
  const translate = useTranslate()
  const record = useRecordContext<McpServerOut>()
  const { setValue } = useFormContext()
  const [fileName, setFileName] = useState<string | null>(null)

  if (record?.tool_source !== 'openapi' && record?.tool_source !== 'odata') return null

  const label = record?.tool_source === 'odata'
    ? translate('data.odataUrl')
    : translate('data.openapiUrl')

  const accept = record?.tool_source === 'odata' ? '.xml' : '.json,.yaml,.yml'

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      setValue('openapi_content', event.target?.result as string)
    }
    reader.readAsText(file)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TextInput
        source="openapi_url"
        label={label}
        fullWidth
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          variant="outlined"
          component="label"
          size="small"
          startIcon={<UploadFileIcon />}
        >
          {translate('data.uploadSpecFile')}
          <input
            type="file"
            hidden
            accept={accept}
            onChange={handleFileChange}
          />
        </Button>
        {fileName && (
          <Typography variant="body2">{fileName}</Typography>
        )}
      </Box>
    </Box>
  )
}

const McpServerEdit: FC = () => {
  const translate = useTranslate()

  return (
    <Edit mutationMode="pessimistic">
      <SimpleForm>
        <TextInput source="display_name" label="data.displayName" fullWidth validate={required()} />
        <TextInput source="name" label="data.mcpUrlSlug" fullWidth helperText={translate('data.mcpUrlSlugHelp')} />
        <TextInput source="description" label="data.description" fullWidth multiline />
        <TextInput source="tool_source" label="data.toolSource" fullWidth disabled />
        <SpecUrlField />
        <BooleanInput source="enabled" label="data.enabled" />
        <BooleanInput source="marketplace" label="data.marketplaceVisible" />
        <TextInput
          source="about"
          label="data.mcpAbout"
          fullWidth
          multiline
          rows={4}
        />
        <TextInput
          source="project_instructions"
          label="data.projectInstructions"
          fullWidth
          multiline
          rows={6}
          helperText={translate('data.projectInstructionsHelp')}
        />
        <AdvancedSettings />
      </SimpleForm>
    </Edit>
  )
}

export default McpServerEdit
